// Email Service for handling form submissions
class EmailService {
  constructor() {
    // Get the base URL and ensure it's HTTP for local development
    let baseURL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
    
    // Force HTTP for localhost in development
    if (baseURL.includes('localhost') && baseURL.startsWith('https://')) {
      baseURL = baseURL.replace('https://', 'http://');
      console.warn('⚠️ Forced HTTPS to HTTP for localhost development');
    }
    
    this.baseURL = baseURL;
    
    // Log for debugging
    console.log('EmailService initialized with baseURL:', this.baseURL);
    console.log('Environment:', process.env.NODE_ENV);
  }

  // Send scholarship form data
  async sendScholarshipForm(formData) {
    console.log('Sending scholarship form data to:', `${this.baseURL}/email/scholarship`);
    try {
      const response = await fetch(`${this.baseURL}/email/scholarship`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          type: 'scholarship',
          data: formData,
          timestamp: new Date().toISOString(),
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      return {
        success: true,
        message: 'Scholarship form submitted successfully!',
        data: result,
      };
    } catch (error) {
      console.error('Error sending scholarship form:', error);
      
      // Provide specific error messages for common issues
      let errorMessage = 'Failed to submit scholarship form. Please try again.';
      
      if (error.message.includes('SSL_PROTOCOL_ERROR') || error.message.includes('ERR_SSL')) {
        errorMessage = 'SSL Error: Make sure the backend server is running on HTTP (not HTTPS) for local development.';
        console.error('🔧 Fix: Start your backend server with: cd edugration-server && npm start');
      } else if (error.message.includes('Failed to fetch')) {
        errorMessage = 'Connection Error: Unable to connect to the backend server. Make sure it\'s running on port 5000.';
        console.error('🔧 Fix: Start your backend server with: cd edugration-server && npm start');
      } else if (error.message.includes('CORS')) {
        errorMessage = 'CORS Error: Backend server CORS configuration issue.';
      }
      
      return {
        success: false,
        message: errorMessage,
        error: error.message,
      };
    }
  }

  // Send contact form data
  async sendContactForm(formData) {
    try {
      const response = await fetch(`${this.baseURL}/email/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          type: 'contact',
          data: formData,
          timestamp: new Date().toISOString(),
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      return {
        success: true,
        message: 'Message sent successfully! We will get back to you soon.',
        data: result,
      };
    } catch (error) {
      console.error('Error sending contact form:', error);
      return {
        success: false,
        message: 'Failed to send message. Please try again.',
        error: error.message,
      };
    }
  }

  // Send callback request
  async sendCallbackRequest(formData) {
    try {
      const response = await fetch(`${this.baseURL}/email/callback`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          type: 'callback',
          data: formData,
          timestamp: new Date().toISOString(),
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      return {
        success: true,
        message: 'Callback request submitted successfully! We will contact you soon.',
        data: result,
      };
    } catch (error) {
      console.error('Error sending callback request:', error);
      return {
        success: false,
        message: 'Failed to submit callback request. Please try again.',
        error: error.message,
      };
    }
  }

  // Generic email sending method
  async sendEmail(emailData) {
    try {
      const response = await fetch(`${this.baseURL}/email/send`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(emailData),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      return {
        success: true,
        message: 'Email sent successfully!',
        data: result,
      };
    } catch (error) {
      console.error('Error sending email:', error);
      return {
        success: false,
        message: 'Failed to send email. Please try again.',
        error: error.message,
      };
    }
  }

  // Validate form data
  validateScholarshipForm(formData) {
    const errors = [];

    if (!formData.name || formData.name.trim().length < 2) {
      errors.push('Name must be at least 2 characters long');
    }

    if (!formData.email || !this.isValidEmail(formData.email)) {
      errors.push('Please enter a valid email address');
    }

    if (!formData.mobile || !this.isValidPhone(formData.mobile)) {
      errors.push('Please enter a valid mobile number');
    }

    if (!formData.studyLevel) {
      errors.push('Please select your study level');
    }

    if (!formData.destination) {
      errors.push('Please select your study destination');
    }

    if (!formData.academicLevel) {
      errors.push('Please select your academic level');
    }

    if (!formData.passingYear || !this.isValidYear(formData.passingYear)) {
      errors.push('Please enter a valid passing year');
    }

    if (!formData.result || formData.result.trim().length < 1) {
      errors.push('Please enter your academic result');
    }

    if (!formData.englishProficiency) {
      errors.push('Please select your English proficiency test');
    }

    if (!formData.overallScore || formData.overallScore.trim().length < 1) {
      errors.push('Please enter your overall score');
    }

    if (!formData.privacyPolicy) {
      errors.push('You must agree to the Privacy Policy');
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  }

  validateContactForm(formData) {
    const errors = [];

    if (!formData.name || formData.name.trim().length < 2) {
      errors.push('Name must be at least 2 characters long');
    }

    if (!formData.email || !this.isValidEmail(formData.email)) {
      errors.push('Please enter a valid email address');
    }

    if (!formData.subject || formData.subject.trim().length < 3) {
      errors.push('Subject must be at least 3 characters long');
    }

    if (!formData.message || formData.message.trim().length < 10) {
      errors.push('Message must be at least 10 characters long');
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  }

  // Helper methods
  isValidEmail(email) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  isValidPhone(phone) {
    const phoneRegex = /^[\+]?[1-9][\d]{0,15}$/;
    return phoneRegex.test(phone.replace(/\s/g, ''));
  }

  isValidYear(year) {
    const currentYear = new Date().getFullYear();
    const yearNum = parseInt(year);
    return yearNum >= 1950 && yearNum <= currentYear;
  }

  // Format form data for email
  formatScholarshipData(formData) {
    return {
      name: formData.name,
      email: formData.email,
      mobile: formData.mobile,
      studyLevel: formData.studyLevel,
      destination: formData.destination,
      academicLevel: formData.academicLevel,
      passingYear: formData.passingYear,
      result: formData.result,
      englishProficiency: formData.englishProficiency,
      overallScore: formData.overallScore,
      privacyPolicy: formData.privacyPolicy,
    };
  }

  formatContactData(formData) {
    return {
      name: formData.name,
      email: formData.email,
      subject: formData.subject,
      message: formData.message,
    };
  }
}

// Create and export a singleton instance
const emailService = new EmailService();
export default emailService;
